import type { ComponentType, ReactNode } from 'react';
import { match } from 'ts-pattern';
import type { z } from 'zod';
import type { Nullish } from '@/types/nullish';
import { PATIENT_FLAGS } from './consts/patient-flags';
import type {
  cdrLabReportHeaderSchema,
  cdrReportSchemas,
  PatientCardResponse,
} from './patient-card-schema';

export const PATIENT_CARD_REPORT_TYPES = [
  'surgery',
  'image',
  'exam',
  'pathology',
] as const;

export type PatientCardReportType = (typeof PATIENT_CARD_REPORT_TYPES)[number];

export type PatientCardFlag = (typeof PATIENT_FLAGS)[number];

export type PatientCardPatient = Pick<
  PatientCardResponse,
  | PatientCardFlag
  | 'mrn'
  | 'patientName'
  | 'genderI18n'
  | 'birthDate'
  | 'icCardRemarkList'
  | 'isolationFlag'
  | 'fastingFlag'
>;

export type PatientCardEncounter = {
  encounterType: string | null;
  visitStartDatetime: string;
  visitEndDatetime: string | null;
  bedNo: string | null;
  roomNo: string | null;
  seqNo: string | null;
  doctorName: string | null;
  divName: string | null;
  diagnosisDesc: string | null;
  icdCode: string | null;
  bodyWeight: number | null;
  effectiveDatetime: string | null;
  surgeryDatetimeList: string[] | null;
  allergenNames: string[] | null;
};

export type PatientCardLabReport = Pick<
  z.infer<typeof cdrLabReportHeaderSchema>,
  | 'requisitionNo'
  | 'orderDatetime'
  | 'orderDoctorName'
  | 'specimenName'
  | 'collectionDatetime'
  | 'receiveDatetime'
  | 'reportDatetime'
  | 'reportDoctorName'
>;

type ImageDisplayKeys =
  | 'requisitionNo'
  | 'orderDatetime'
  | 'orderDoctorName'
  | 'executeDateTime'
  | 'executeDoctorName'
  | 'reportName'
  | 'reportDatetime'
  | 'reportPerformerName'
  | 'conclusion';

export type PatientCardReportContent = {
  surgery: Pick<
    z.infer<typeof cdrReportSchemas.surgery>,
    | 'formCode'
    | 'orderDatetime'
    | 'executeDateTime'
    | 'surgeryNameZh'
    | 'executeDoctorName'
    | 'reportDatetime'
  >;
  image: Pick<z.infer<typeof cdrReportSchemas.image>, ImageDisplayKeys>;
  exam: Pick<z.infer<typeof cdrReportSchemas.exam>, ImageDisplayKeys>;
  pathology: Pick<
    z.infer<typeof cdrReportSchemas.pathology>,
    | 'requisitionNo'
    | 'pathologyCode'
    | 'orderDatetime'
    | 'orderDoctorName'
    | 'executeDateTime'
    | 'executeDoctorName'
    | 'submitterName'
    | 'divName'
    | 'receiveDatetime'
    | 'reportDatetime'
    | 'reportPerformerName'
  >;
};

export type PatientCardReport = {
  [K in PatientCardReportType]: {
    reportType: K;
    data: PatientCardReportContent[K] | Nullish;
  };
}[PatientCardReportType];

export type PatientCardInput = {
  mrn: string;
  encounterId?: string;
  reportId?: string;
  reportType?: PatientCardReportType;
};

type QuerySnapshot<T> = {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
};

type RequestedQuery<T> = {
  requested: boolean;
  query: QuerySnapshot<T>;
};

type ReportContentQuery =
  | {
      [K in PatientCardReportType]: {
        reportType: K;
        query: QuerySnapshot<PatientCardReportContent[K] | null>;
      };
    }[PatientCardReportType]
  | { reportType: undefined; query: QuerySnapshot<null> };

export type PatientCardSourceResult = {
  patient: QuerySnapshot<PatientCardPatient | null>;
  encounter: QuerySnapshot<PatientCardEncounter>;
  details?: {
    labReport: RequestedQuery<PatientCardLabReport>;
    reportContent: { requested: boolean } & ReportContentQuery;
  };
};

export type PatientCardSourceProps = PatientCardInput & {
  children: (result: PatientCardSourceResult) => ReactNode;
};

export type PatientCardSource = ComponentType<PatientCardSourceProps>;

export type PatientCardData = {
  patientData: PatientCardPatient | Nullish;
  encounterData: PatientCardEncounter | undefined;
  labReportData: PatientCardLabReport | undefined;
  report: PatientCardReport | undefined;
  patientFlags: PatientCardFlag[];
  isPatientLoading: boolean;
  isPatientError: boolean;
  isEncounterInfoLoading: boolean;
  isEncounterInfoError: boolean;
};

const isRequestedLoading = ({ requested, query }: RequestedQuery<unknown>) =>
  requested && query.isLoading;

const isRequestedError = ({ requested, query }: RequestedQuery<unknown>) =>
  requested && !query.data && query.isError;

const toPatientCardReport = (reportContent: ReportContentQuery) =>
  match<ReportContentQuery, PatientCardReport | undefined>(reportContent)
    .with({ reportType: 'surgery' }, ({ reportType, query }) => ({
      reportType,
      data: query.data,
    }))
    .with({ reportType: 'image' }, ({ reportType, query }) => ({
      reportType,
      data: query.data,
    }))
    .with({ reportType: 'exam' }, ({ reportType, query }) => ({
      reportType,
      data: query.data,
    }))
    .with({ reportType: 'pathology' }, ({ reportType, query }) => ({
      reportType,
      data: query.data,
    }))
    .with({ reportType: undefined }, () => undefined)
    .exhaustive();

export const derivePatientCardData = (
  { patient, encounter, details }: PatientCardSourceResult,
  { encounterId }: Pick<PatientCardInput, 'encounterId'>,
): PatientCardData => {
  const requestedQueries: RequestedQuery<unknown>[] = [
    { requested: Boolean(encounterId), query: encounter },
    ...(details ? [details.labReport, details.reportContent] : []),
  ];
  const patientData = patient.data;

  return {
    patientData,
    encounterData: encounter.data,
    labReportData: details?.labReport.query.data,
    report: details && toPatientCardReport(details.reportContent),
    patientFlags: patientData
      ? PATIENT_FLAGS.filter((flag) => patientData[flag])
      : [],
    isPatientLoading: patient.isLoading,
    isPatientError: patient.isError,
    isEncounterInfoLoading: requestedQueries.some(isRequestedLoading),
    isEncounterInfoError: requestedQueries.some(isRequestedError),
  };
};
